import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Wizard,
  Layout,
  LayoutContainer,
  StepContainer,
} from '@bingads-webui-react/wizard';
import { BusyBlocker, CancelWizardDialog, ConfirmWizardDialog, DefaultTemplate } from '@bingads-webui-react/wizard-ui-library';
import { withSiteMap, getRoutePathById } from '@bingads-webui-campaign-react/with-site-map';
import { WizardPerfMarker } from '@bingads-webui-campaign/wizard-performance-marker';
import { WizardStepLogger } from '@bingads-webui-campaign-react/step-wizard-logger';
import { Activity, Scenario, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { ContentFooter } from '@bingads-webui-campaign-react/content-footer';
import { useConfirmDialogConfigWithLogger } from '@bingads-webui-campaign-react/campaign-settings-wizard-v1-utils';

import { EditFooter } from '../shared/edit-footer';
import { SingleStepLoggerUnderActivity } from '../shared/logger-under-activity';
import { SEARCH_WIZARD_TYPE_NAME } from './constants';

const EditWizardInternal = (props) => {
  const {
    i18n,
    stepConfigs,
    perfMarker,
    scope,
    siteMap,
    wizardTypeName,
    wizardLoggerFlowName,
    wizardLoggerSubFlowName,
    wizardLoggerStepToPage,
    perfMarkerName,
    permissions,
    onDoneNavigateToSiteMapId,
  } = props;
  const wizardPerfMarker = useMemo(() => (
    perfMarker.createChild(perfMarkerName)
  ), [perfMarker, perfMarkerName]);
  const isConversionAssociationWarningP2Enabled = permissions && permissions.dynamic && permissions.dynamic.ConversionAssociationWarningP2;
  const shouldHaveConfirmationDialog = isConversionAssociationWarningP2Enabled && wizardTypeName === SEARCH_WIZARD_TYPE_NAME.SEARCH_CAMPAIGN_EDIT_WIZARD;

  const routeToNavigateToWhenDone = useMemo(() => getRoutePathById(siteMap, onDoneNavigateToSiteMapId), [onDoneNavigateToSiteMapId, siteMap]);

  const activity = useContext(ActivityContext);
  const writeWizardLog = useCallback((...args) => {
    activity.signal(...args);
  }, [activity]);

  const config = useMemo(() => {
    const newStepConfigs = stepConfigs.map(stepConfig => ({
      ...stepConfig,
      view: viewProps => (
        <>
          {stepConfig.view({ ...viewProps, wizardPerfMarker })}
          <SingleStepLoggerUnderActivity />
        </>
      ),
    }));

    return {
      id: wizardTypeName || 'search-edit-wizard',
      steps: newStepConfigs,
      actions: {
        onDone: () => scope.save(routeToNavigateToWhenDone),
        onExited: () => scope.save(routeToNavigateToWhenDone),
      },
    };
  }, [routeToNavigateToWhenDone, scope, stepConfigs, wizardPerfMarker, wizardTypeName]);

  return (
    <Wizard config={config}>
      <WizardStepLogger
        writeLog={writeWizardLog}
        flow={wizardLoggerFlowName}
        subflow={wizardLoggerSubFlowName}
        stepToPage={wizardLoggerStepToPage}
        shouldPost
      >
        <WizardPerfMarker wizardName={perfMarkerName} wizardMarker={wizardPerfMarker}>
          <LayoutContainer template={DefaultTemplate}>
            <Layout type="Blocker" component={BusyBlocker} />
            <Layout type="StepContainer" component={StepContainer} />
            <Layout
              type="Footer"
              component={EditFooter}
              saveAndExitButtonText={i18n.getString(_TL_('Save'))}
              cancelButtonText={i18n.getString(_TL_('Cancel'))}
            />
            <Layout type="CancelDialog" component={CancelWizardDialog} />
            {shouldHaveConfirmationDialog && <Layout type="ConfirmDialog" component={ConfirmWizardDialog} config={useConfirmDialogConfigWithLogger} />}
          </LayoutContainer>
        </WizardPerfMarker>
        <ContentFooter />
      </WizardStepLogger>
    </Wizard>
  );
};

EditWizardInternal.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  stepConfigs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    view: PropTypes.func.isRequired,
  })).isRequired,
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    save: PropTypes.func.isRequired,
  }).isRequired,
  siteMap: PropTypes.shape({}).isRequired,
  wizardTypeName: PropTypes.string.isRequired,
  wizardLoggerFlowName: PropTypes.string.isRequired,
  wizardLoggerSubFlowName: PropTypes.string.isRequired,
  wizardLoggerStepToPage: PropTypes.shape({}).isRequired,
  perfMarkerName: PropTypes.string.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  onDoneNavigateToSiteMapId: PropTypes.string.isRequired,
};

export const EditWizardWithActivity = props => (
  <Scenario name={props.scenarioName}>
    <Activity name={props.activityName}>
      <EditWizardInternal {...props} />
    </Activity>
  </Scenario>
);

EditWizardWithActivity.propTypes = {
  scenarioName: PropTypes.string.isRequired,
  activityName: PropTypes.string.isRequired,
};

export const EditWizard = withSiteMap(EditWizardWithActivity);
